import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { T } from "@/lib/testIds";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { RefreshCw, Users, AlertTriangle, Clock, IndianRupee, TrendingUp, ArrowRight } from "lucide-react";
import OnboardingChecklist from "@/components/OnboardingChecklist";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n || 0));

function Kpi({ testId, icon: Icon, label, value, sub, tone = "default" }) {
  const tones = {
    default: "text-[#0A0A0B]",
    warn: "text-[#B45309]",
    danger: "text-[#B91C1C]",
    good: "text-[#15803D]",
  };
  return (
    <div className="p-6 bg-white border co-hairline flex flex-col justify-between min-h-[140px] co-fade-in" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="co-label">{label}</span>
        <Icon className="h-4 w-4 text-[#52525B]" strokeWidth={1.75} aria-hidden="true" />
      </div>
      <div>
        <div className={`font-heading text-3xl lg:text-4xl font-bold tracking-tighter ${tones[tone]}`}>
          {value}
        </div>
        {sub && <div className="text-xs text-[#52525B] mt-1">{sub}</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { workspace } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/dashboard/summary");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runSweep = async () => {
    setSweeping(true);
    try {
      const r = await api.post("/reminders/sweep-now");
      const n = r.data.logs_created || 0;
      if (n > 0) {
        toast.success(`${n} reminder(s) sent to ${r.data.clients_processed} client(s)`);
      } else {
        toast.info(
          "No reminders fired today — no client is due within your schedule window (default 7 / 3 / 0 days). Use 'Send to all pending' to nudge every unpaid client right now.",
          { duration: 6000 },
        );
      }
      await load();
    } catch (e) {
      toast.error("Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const forceAll = async () => {
    if (!window.confirm("Send a reminder to EVERY unpaid client right now? This bypasses your day-before schedule.")) return;
    setSweeping(true);
    try {
      const r = await api.post("/reminders/sweep-now?force_all=true");
      const n = r.data.logs_created || 0;
      toast.success(`${n} reminder(s) fired to ${r.data.clients_processed} pending client(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Force-send failed");
    } finally {
      setSweeping(false);
    }
  };

  const kpis = data?.kpis;

  return (
    <div>
      <header className="co-glass-header sticky top-0 z-20 px-6 lg:px-10 py-5 flex items-center justify-between">
        <div>
          <div className="co-label">Overview</div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold tracking-tight mt-1">
            Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid={T.triggerSweepBtn}
            onClick={runSweep}
            disabled={sweeping}
            className="inline-flex items-center gap-2 bg-[#0A0A0B] text-white px-4 py-2 text-sm font-semibold rounded-sm co-btn-press hover:bg-[#27272A] transition-colors duration-200 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${sweeping ? "animate-spin" : ""}`} strokeWidth={2} aria-hidden="true" />
            {sweeping ? "Sending…" : "Run reminders now"}
          </button>
          <button
            data-testid="dashboard-force-all-btn"
            onClick={forceAll}
            disabled={sweeping}
            title="Fire a reminder for every unpaid client right now — bypasses the 7/3/0 schedule."
            className="inline-flex items-center gap-2 bg-white border border-[#0A0A0B] text-[#0A0A0B] px-4 py-2 text-sm font-semibold rounded-sm co-btn-press hover:bg-[#F4F4F5] transition-colors duration-200 disabled:opacity-60"
          >
            Send to all pending
          </button>
        </div>
      </header>

      <div className="p-6 lg:p-10">
        {loading ? (
          <div className="text-sm text-[#52525B]">Loading dashboard…</div>
        ) : (
          <>
            <OnboardingChecklist />

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-0 border co-hairline bg-white mt-6">
              <Kpi
                testId={T.kpiPendingAmount}
                icon={IndianRupee}
                label="Pending amount"
                value={fmtINR(kpis?.pending_amount)}
                sub="Across all active clients"
              />
              <Kpi
                testId={T.kpiCollectedMonth}
                icon={TrendingUp}
                label="Collected · this month"
                value={fmtINR(kpis?.collected_month)}
                tone="good"
                sub="Auto + manual confirmations"
              />
              <Kpi
                testId={T.kpiTotalClients}
                icon={Users}
                label="Active clients"
                value={kpis?.total_clients ?? 0}
              />
              <Kpi
                testId={T.kpiUpcoming}
                icon={Clock}
                label="Upcoming · 7 days"
                value={kpis?.upcoming_7d ?? 0}
                tone="warn"
              />
              <Kpi
                testId={T.kpiOverdue}
                icon={AlertTriangle}
                label="Overdue"
                value={kpis?.overdue ?? 0}
                tone="danger"
              />
            </div>

            {/* Chart + recent logs */}
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-0 border co-hairline bg-white">
              <div className="lg:col-span-2 p-6 lg:p-8 lg:border-r co-hairline" data-testid={T.chartCollections}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="co-label">Collections trend</div>
                    <div className="font-heading text-xl font-bold tracking-tight mt-1">
                      Last 6 months
                    </div>
                  </div>
                </div>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data?.series || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" stroke="#52525B" fontSize={12} />
                      <YAxis stroke="#52525B" fontSize={12} tickFormatter={(v) => (v >= 1000 ? `${v / 1000}k` : v)} />
                      <Tooltip
                        contentStyle={{ background: "#0A0A0B", border: "none", color: "white", borderRadius: 4, fontSize: 12 }}
                        formatter={(v) => fmtINR(v)}
                      />
                      <Line type="monotone" dataKey="amount" stroke="#4338CA" strokeWidth={2} dot={{ fill: "#4338CA", r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="p-6 lg:p-8">
                <div className="co-label">Recent reminders</div>
                <div className="font-heading text-xl font-bold tracking-tight mt-1 mb-4">
                  Activity
                </div>
                {(data?.recent_reminders || []).length === 0 ? (
                  <div className="text-sm text-[#52525B]">No reminders sent yet.</div>
                ) : (
                  <ul className="space-y-3">
                    {data.recent_reminders.map((r) => (
                      <li key={r.id} className="flex items-start gap-3 text-sm">
                        <span
                          className={`co-pill ${
                            r.status === "sent"
                              ? "co-pill-sent"
                              : r.status === "failed"
                              ? "co-pill-failed"
                              : "co-pill-mock"
                          }`}
                        >
                          {r.channel === "email" ? "EMAIL" : "WHATSAPP"} · {r.status.toUpperCase()}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{r.client_name}</div>
                          <div className="text-xs text-[#52525B]">
                            {new Date(r.sent_at).toLocaleString()}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <Link
                  to="/app/reminders"
                  className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-[#4338CA] hover:text-[#3730A3]"
                >
                  View full log <ArrowRight className="h-3 w-3" strokeWidth={2} />
                </Link>
              </div>
            </div>

            {/* Quick actions */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-0 border co-hairline bg-white">
              {[
                { to: "/app/clients", t: "Add or import clients", d: "Manual entry or CSV upload." },
                {
                  to: "/app/payment-options",
                  t:
                    workspace?.business_type === "distributor"
                      ? "Add insurer / AMC links"
                      : "Configure payment options",
                  d:
                    workspace?.business_type === "distributor"
                      ? "Pick from 24+ providers or add custom links."
                      : "Razorpay, UPI, QR or external link.",
                },
                { to: "/app/reminder-rules", t: "Tune reminder cadence", d: "Days-before triggers & template." },
              ].map((a, i) => (
                <Link
                  key={a.to}
                  to={a.to}
                  className={`p-6 lg:p-8 hover:bg-[#FAFAFA] transition-colors duration-200 ${
                    i < 2 ? "md:border-r co-hairline" : ""
                  } ${i < 2 ? "border-b md:border-b-0" : ""}`}
                >
                  <div className="co-label">Quick action</div>
                  <div className="font-heading text-lg font-bold tracking-tight mt-2 flex items-center gap-2">
                    {a.t}
                    <ArrowRight className="h-4 w-4 text-[#4338CA]" strokeWidth={2} />
                  </div>
                  <div className="text-sm text-[#52525B] mt-1">{a.d}</div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
